import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Map as GoogleMap, InfoWindow, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import type { Marker, Cluster } from '@googlemaps/markerclusterer';
import type { MarkerData } from '../types/markers';
import { HTTP_API_URL } from '../config/api';
import './GoogleMapsOverrides.css';

type ClustererMarker = google.maps.marker.AdvancedMarkerElement & Marker & {
    markerData?: MarkerData;
};

const getMarkerData = (marker: Marker): MarkerData | undefined =>
    (marker as ClustererMarker).markerData;

type ClusterGroup = {
    key: string;
    markers: MarkerData[];
    selectedMarkerId: string;
};

type ClusterSelection = {
    isCluster: true;
    lat: number;
    lng: number;
    markerIds: string[];
    totalCount: number;
    groups: ClusterGroup[];
};

type SelectedItem =
    | ClusterSelection
    | (MarkerData & { isCluster: false; totalCount?: number });

const buildAbsoluteMediaUrl = (value?: string | null): string | null => {
    if (!value) {
        return null;
    }
    if (/^(https?:|data:)/i.test(value)) {
        return value;
    }
    const normalized = value.startsWith('/') ? value : `/${value}`;
    return `${HTTP_API_URL}${normalized}`;
};

const resolveMarkerThumbnail = (marker: MarkerData): string | null => {
    // 1. Try new thumbnail_url (path o signed URL)
    if (marker.thumbnail_url) {
        if (/^(https?:|data:)/i.test(marker.thumbnail_url)) {
            return marker.thumbnail_url;
        }
        const normalized = marker.thumbnail_url.startsWith('/')
            ? marker.thumbnail_url
            : `/gcs-images/${marker.thumbnail_url}`;
        return buildAbsoluteMediaUrl(normalized);
    }

    // 2. Fallback: legacy base64 thumbnail (for backward compatibility)
    if (marker.thumbnail) {
        return marker.thumbnail;
    }

    // 3. Final fallback: full image URL
    // Prefer generating fresh URL from photo_path if available (fixes expired URLs and localhost issues)
    if (marker.photo_path) {
        return buildAbsoluteMediaUrl(`/gcs-images/${marker.photo_path}`);
    }

    const imageUrl = marker.imageUrl;
    return buildAbsoluteMediaUrl(imageUrl);
};

const createDefaultPin = (label?: string, isMoving?: boolean, isSource?: boolean): HTMLElement | null => {
    const PinClass = (google as any)?.maps?.marker?.PinElement;
    // Source: Orange (#FBBC04), Moving: Orange (#FBBC04), Default: Blue (#2563eb)
    const background = (isMoving || isSource) ? '#FBBC04' : '#2563eb';
    const borderColor = (isMoving || isSource) ? '#000000' : '#ffffff';
    const glyphColor = (isMoving || isSource) ? '#000000' : '#ffffff';
    if (!PinClass) {
        const el = document.createElement('div');
        el.style.width = '28px';
        el.style.height = '28px';
        el.style.borderRadius = '50%';
        el.style.background = background;
        el.style.border = `3px solid ${borderColor}`;
        el.style.boxShadow = '0 6px 16px rgba(0,0,0,0.25)';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.color = glyphColor;
        el.style.fontWeight = '700';
        el.textContent = label ? label.charAt(0).toUpperCase() : '';
        return el;
    }
    const pin = new PinClass({
        background,
        borderColor,
        glyphColor,
        glyphText: label ? label.charAt(0).toUpperCase() : undefined,
        scale: 1.1,
    });
    return pin.element ?? (pin as unknown as HTMLElement);
};

interface MapProps {
    markers: MarkerData[];
    onImageClick: (marker: MarkerData) => void;
    onMarkerDragEnd: (marker: MarkerData, newPosition: { lat: number; lng: number }) => void;
    onRequestDeleteMarker: (marker: MarkerData) => void;
    onRequestDeleteDuplicates: (marker: MarkerData) => void;
    isDuplicateMode: boolean;
    onRequestMoveMarker: (marker: MarkerData) => Promise<boolean> | boolean;
    onRequestMoreLikeThis: (marker: MarkerData) => void;
    showMoreLikeThis: boolean;
    pendingMoveTarget: MarkerData | null;
    onConsumePendingMove: () => void;
    onCrosshairModeChange?: (active: boolean) => void;
    sourceMarkerId?: string | null;
    autoFitToken?: number;
    fitMarkers?: MarkerData[];
    isAutoZoomEnabled?: boolean;
}

const center = {
    lat: 41.9027835,
    lng: 12.4963655 // Center of Rome, as a default
};
const CROSSHAIR_ZOOM_LOCK_KEY = 'map_zoom_lock_crosshair';
const GOOGLE_MAPS_MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';

const MapComponent: React.FC<MapProps> = ({
    markers: allMarkers,
    onImageClick,
    onMarkerDragEnd,
    onRequestDeleteMarker,
    onRequestDeleteDuplicates,
    isDuplicateMode,
    onRequestMoveMarker,
    onRequestMoreLikeThis,
    showMoreLikeThis,
    pendingMoveTarget,
    onConsumePendingMove,
    onCrosshairModeChange,
    sourceMarkerId,
    autoFitToken,
    fitMarkers = [],
    isAutoZoomEnabled = true,
}) => {
    const map = useMap();
    const markerLibrary = useMapsLibrary('marker');
    const clusterer = useRef<MarkerClusterer | null>(null);
    const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
    const [activeActionsForId, setActiveActionsForId] = useState<string | null>(null);
    const [isCoarsePointer, setIsCoarsePointer] = useState(false);
    const [isPortrait, setIsPortrait] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.innerHeight >= window.innerWidth;
    });
    const [isCrosshairZoomLocked, setIsCrosshairZoomLocked] = useState<boolean>(() => {
        if (typeof window === 'undefined') return true;
        try {
            const stored = window.sessionStorage.getItem(CROSSHAIR_ZOOM_LOCK_KEY);
            if (stored === null) return true;
            return stored === 'true';
        } catch {
            return true;
        }
    });
    // Tracks the marker currently being moved via the crosshair flow.
    const [ghostMoveTarget, setGhostMoveTarget] = useState<MarkerData | null>(null);
    const [movingMarkerId, setMovingMarkerId] = useState<string | null>(null);
    const previewPanelRef = useRef<HTMLDivElement | null>(null);
    // Used to ignore dragstart events fired while the user is interacting with the info window on touch devices.
    const previewInteractionRef = useRef(false);
    const crosshairCenterRef = useRef<google.maps.LatLngLiteral | null>(null);

    // Prevent Google Maps from stealing pointer/click events coming from the InfoWindow content.
    const stopMapEvent = useCallback((event: any) => {
        if (!event) return;
        if (typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }
        const nativeEvent = (event as any)?.nativeEvent;
        if (nativeEvent?.stopImmediatePropagation) {
            nativeEvent.stopImmediatePropagation();
        }
    }, []);

    const haltMapEvent = useCallback((event: any) => {
        stopMapEvent(event);
        if (typeof event.preventDefault === 'function') {
            event.preventDefault();
        }
    }, [stopMapEvent]);

    useEffect(() => {
        if (!window.matchMedia) return;
        const mediaQuery = window.matchMedia('(pointer: coarse)');
        setIsCoarsePointer(mediaQuery.matches);
        const handler = (e: MediaQueryListEvent) => setIsCoarsePointer(e.matches);
        // Feature-detect addEventListener and fall back to addListener for older browsers
        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handler);
        } else {
            // Fallback for older Safari and Android WebView
            (mediaQuery as any).addListener(handler);
        }
        return () => {
            // Cleanup with fallback for older browsers
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handler);
            } else {
                // Fallback for older Safari and Android WebView
                (mediaQuery as any).removeListener(handler);
            }
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsPortrait(window.innerHeight >= window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, []);

    useEffect(() => {
        try {
            window.sessionStorage.setItem(CROSSHAIR_ZOOM_LOCK_KEY, String(isCrosshairZoomLocked));
        } catch {
            // ignore persistence errors (e.g. private mode)
        }
    }, [isCrosshairZoomLocked]);

    const markersRef = useRef<{ [key: string]: ClustererMarker }>({});
    const markersDataRef = useRef<MarkerData[]>([]);
    const hasAdvancedMarker = Boolean(
        markerLibrary && (globalThis as any)?.google?.maps?.marker?.AdvancedMarkerElement
    );
    const getGroupKey = (marker: MarkerData): string =>
        marker.contentHash || marker.photoId || marker.id;

    const forceMapRepaint = useCallback(() => {
        if (!map) return;
        try {
            // Recover from gray/blank map when init happens before final layout sizing.
            google.maps.event.trigger(map, 'resize');
            const centerNow = map.getCenter?.();
            if (centerNow) {
                map.setCenter(centerNow);
            }
        } catch {
            // no-op
        }
    }, [map]);

    const buildClusterGroups = React.useCallback((items: MarkerData[], previous?: ClusterGroup[]): ClusterGroup[] => {
        const mapGroups = new Map<string, MarkerData[]>();
        items.forEach((marker) => {
            const key = getGroupKey(marker);
            const current = mapGroups.get(key) || [];
            current.push(marker);
            mapGroups.set(key, current);
        });

        const previousSelection = new Map(
            (previous ?? []).map((group) => [group.key, group.selectedMarkerId])
        );

        return Array.from(mapGroups.entries()).map(([key, markers]) => {
            const prevSelected = previousSelection.get(key);
            const selectedMarkerId =
                prevSelected && markers.some((marker) => marker.id === prevSelected)
                    ? prevSelected
                    : markers[0].id;
            return {
                key,
                markers,
                selectedMarkerId,
            };
        });
    }, []);

    const groupsAreDifferent = (incoming: ClusterGroup[], prevGroups: ClusterGroup[]) => {
        if (incoming.length !== prevGroups.length) {
            return true;
        }
        for (let i = 0; i < incoming.length; i++) {
            const nextGroup = incoming[i];
            const prevGroup = prevGroups[i];
            if (!prevGroup || prevGroup.key !== nextGroup.key) {
                return true;
            }
            if (prevGroup.selectedMarkerId !== nextGroup.selectedMarkerId) {
                return true;
            }
            if (prevGroup.selectedMarkerId !== nextGroup.selectedMarkerId) {
                return true;
            }
            if (prevGroup.markers.length !== nextGroup.markers.length) {
                return true;
            }
            for (let j = 0; j < nextGroup.markers.length; j++) {
                if (nextGroup.markers[j]?.id !== prevGroup.markers[j]?.id) {
                    return true;
                }
            }
        }
        return false;
    };

    const getMarkersAtSamePosition = useCallback(
        (target?: MarkerData | null): MarkerData[] => {
            if (!target) {
                return [];
            }
            return markersDataRef.current.filter(
                (marker: MarkerData) => marker.lat === target.lat && marker.lng === target.lng
            );
        },
        []
    );

    const openMarkerDetails = useCallback((markerData: MarkerData) => {
        const markersAtSamePosition = getMarkersAtSamePosition(markerData);
        const latestMarker =
            markersAtSamePosition.find((marker) => marker.id === markerData.id) ||
            markersAtSamePosition[0] ||
            markerData;

        if (markersAtSamePosition.length > 1) {
            const groups = buildClusterGroups(markersAtSamePosition);
            setSelectedItem({
                isCluster: true,
                lat: latestMarker.lat,
                lng: latestMarker.lng,
                markerIds: markersAtSamePosition.map((marker) => marker.id),
                totalCount: markersAtSamePosition.length,
                groups,
            });
            return;
        }

        setSelectedItem({
            ...latestMarker,
            isCluster: false,
            totalCount: markersAtSamePosition.length || 1,
        });
    }, [buildClusterGroups, getMarkersAtSamePosition]);

    const toggleActionsForMarker = (markerData: MarkerData) => {
        if (!markerData) return;
        setActiveActionsForId((prev) => (prev === markerData.id ? null : markerData.id));
    };

    const handleMoveMarkerClick = useCallback(async (markerData: MarkerData) => {
        console.log('[MAP][ACTION] Move clicked', markerData.id, markerData.filename);
        if (!markerData || markerData.draggable === false) {
            return;
        }
        try {
            const allowed = await onRequestMoveMarker(markerData);
            if (!allowed) {
                return;
            }
        } catch {
            return;
        }
        setSelectedItem(null);
        setActiveActionsForId(null);
        setGhostMoveTarget(markerData);
        setMovingMarkerId(markerData.id);
        if (markerData.lat && markerData.lng && map) {
            map.panTo?.({ lat: markerData.lat, lng: markerData.lng });
        }
    }, [map, onRequestMoveMarker]);

    const handleDeleteMarkerClick = (marker: MarkerData) => {
        console.log('[MAP][ACTION] Delete clicked', marker.id, marker.filename);
        setSelectedItem(null);
        setActiveActionsForId(null);
        onRequestDeleteMarker(marker);
    };

    const handleDeleteDuplicatesClick = (marker: MarkerData) => {
        setSelectedItem(null);
        setActiveActionsForId(null);
        onRequestDeleteDuplicates(marker);
    };

    const handleGhostMoveConfirm = useCallback(() => {
        if (!ghostMoveTarget || !map) {
            setGhostMoveTarget(null);
            return;
        }
        const center = map.getCenter?.();
        if (!center) {
            setGhostMoveTarget(null);
            return;
        }
        // Commit the move to the map center picked by the crosshair.
        onMarkerDragEnd(ghostMoveTarget, { lat: center.lat(), lng: center.lng() });
        setGhostMoveTarget(null);
        setMovingMarkerId(null);
        setSelectedItem(null);
        setActiveActionsForId(null);
    }, [ghostMoveTarget, map, onMarkerDragEnd]);

    const handleGhostMoveCancel = useCallback(() => {
        setGhostMoveTarget(null);
        setMovingMarkerId(null);
    }, []);

    const closePreview = useCallback(() => {
        setSelectedItem(null);
        setActiveActionsForId(null);
    }, []);

    const handleMarkerSelect = useCallback((markerData: MarkerData) => {
        openMarkerDetails(markerData);
    }, [openMarkerDetails]);

    useEffect(() => {
        onCrosshairModeChange?.(Boolean(ghostMoveTarget));
    }, [ghostMoveTarget, onCrosshairModeChange]);

    useEffect(() => {
        if (!map) return;
        if (!autoFitToken) return;
        if (!isAutoZoomEnabled) return;
        if (ghostMoveTarget) return;

        const items = (fitMarkers || []).filter((marker) =>
            typeof marker.lat === 'number' &&
            typeof marker.lng === 'number' &&
            !Number.isNaN(marker.lat) &&
            !Number.isNaN(marker.lng)
        );
        if (!items.length) return;

        if (items.length === 1) {
            const { lat, lng } = items[0];
            map.setCenter({ lat, lng });
            const targetZoom = 13;
            map.setZoom?.(targetZoom);
            return;
        }

        const bounds = new google.maps.LatLngBounds();
        items.forEach(({ lat, lng }) => bounds.extend({ lat, lng }));
        map.fitBounds(bounds, {
            top: 40,
            right: 40,
            bottom: 140,
            left: 40,
        });
    }, [autoFitToken, fitMarkers, ghostMoveTarget, isAutoZoomEnabled, map]);

    useEffect(() => {
        if (!map) return;
        const updateCenterRef = () => {
            const mapCenter = map.getCenter?.();
            if (mapCenter) {
                crosshairCenterRef.current = mapCenter.toJSON();
            }
        };
        const idleListener = map.addListener('idle', updateCenterRef);
        const clickListener = map.addListener('click', () => {
            // User requested that clicking map should NOT close popup.
            // Only the 'X' button or explicit cancel actions close it.
            // keeping this listener empty or logging if needed for debug.
            console.log('[MAP] Clicked on map (popup remains open)');
        });
        updateCenterRef();
        return () => {
            idleListener.remove();
            clickListener.remove();
        };
    }, [map]);

    useEffect(() => {
        if (!map) return;

        const mapDiv = map.getDiv?.();
        if (!mapDiv) return;

        const kick = () => forceMapRepaint();

        const t0 = window.setTimeout(kick, 0);
        const t1 = window.setTimeout(kick, 300);
        const t2 = window.setTimeout(kick, 1200);

        const onVisibility = () => {
            if (!document.hidden) {
                kick();
            }
        };
        document.addEventListener('visibilitychange', onVisibility);

        const hasResizeObserver = typeof ResizeObserver !== 'undefined';
        const resizeObserver = hasResizeObserver
            ? new ResizeObserver(() => {
                kick();
            })
            : null;
        if (resizeObserver) {
            resizeObserver.observe(mapDiv);
        } else {
            window.addEventListener('resize', kick);
        }

        return () => {
            window.clearTimeout(t0);
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            document.removeEventListener('visibilitychange', onVisibility);
            if (resizeObserver) {
                resizeObserver.disconnect();
            } else {
                window.removeEventListener('resize', kick);
            }
        };
    }, [map, forceMapRepaint]);

    useEffect(() => {
        if (!map || !ghostMoveTarget || !isCrosshairZoomLocked) {
            return;
        }

        const mapDiv = map.getDiv();
        const onWheel = (event: WheelEvent) => {
            if (!crosshairCenterRef.current) {
                return;
            }
            event.preventDefault();
            const zoomStep = 0.3; // Match the gentler native zoom feeling
            const delta = event.deltaY < 0 ? zoomStep : -zoomStep;
            map.setCenter(crosshairCenterRef.current);
            const currentZoom = map.getZoom() ?? 0;
            map.setZoom(currentZoom + delta);
        };

        if (mapDiv) {
            mapDiv.addEventListener('wheel', onWheel, { passive: false });
        }

        const zoomListener = map.addListener('zoom_changed', () => {
            if (crosshairCenterRef.current) {
                map.setCenter(crosshairCenterRef.current);
            }
        });

        return () => {
            if (mapDiv) {
                mapDiv.removeEventListener('wheel', onWheel);
            }
            zoomListener.remove();
        };
    }, [map, ghostMoveTarget, isCrosshairZoomLocked]);

    // Trigger crosshair flow from external requests (e.g., "Create marker" from unmarked modal)
    useEffect(() => {
        if (!pendingMoveTarget) {
            return;
        }
        setSelectedItem(null);
        setActiveActionsForId(null);
        setGhostMoveTarget(pendingMoveTarget);
        setMovingMarkerId(pendingMoveTarget.id);
        const hasCoords =
            typeof pendingMoveTarget.lat === 'number' &&
            !Number.isNaN(pendingMoveTarget.lat) &&
            typeof pendingMoveTarget.lng === 'number' &&
            !Number.isNaN(pendingMoveTarget.lng);
        const targetLat = hasCoords ? pendingMoveTarget.lat : center.lat;
        const targetLng = hasCoords ? pendingMoveTarget.lng : center.lng;
        if (map?.panTo) {
            map.panTo({ lat: targetLat, lng: targetLng });
        }
        if (map?.setZoom && !hasCoords) {
            map.setZoom(3);
        }
        onConsumePendingMove();
    }, [pendingMoveTarget, map, onConsumePendingMove]);

    // When entering Move mode, ensure the bottom controls/crosshair are visible in viewport
    useEffect(() => {
        if (!ghostMoveTarget || typeof window === 'undefined') {
            return;
        }
        const mapDiv = map?.getDiv?.();
        if (!mapDiv || typeof mapDiv.scrollIntoView !== 'function') return;
        requestAnimationFrame(() => {
            const rect = mapDiv.getBoundingClientRect();
            const needsScroll = rect.bottom > window.innerHeight || rect.top < 0;
            if (needsScroll) {
                mapDiv.scrollIntoView({ block: 'end', behavior: 'smooth' });
            }
        });
    }, [ghostMoveTarget, map]);

    useEffect(() => {
        markersDataRef.current = allMarkers;
        setSelectedItem((prev) => {
            if (!prev) {
                return prev;
            }

            if (prev.isCluster) {
                const items = prev.markerIds
                    .map((id) => allMarkers.find((marker) => marker.id === id))
                    .filter((marker): marker is MarkerData => Boolean(marker));

                if (!items.length) {
                    return null;
                }

                const totalCount = items.length;
                const groups = buildClusterGroups(items, prev.groups);

                if (
                    totalCount !== prev.totalCount ||
                    groupsAreDifferent(groups, prev.groups)
                ) {
                    return {
                        ...prev,
                        lat: items[0].lat,
                        lng: items[0].lng,
                        totalCount,
                        groups,
                    };
                }
                return prev;
            }

            const updatedMarker = allMarkers.find((marker) => marker.id === prev.id);
            if (!updatedMarker) {
                return null;
            }

            const hasChanged =
                updatedMarker.lat !== prev.lat ||
                updatedMarker.lng !== prev.lng ||
                updatedMarker.filename !== prev.filename ||
                updatedMarker.imageUrl !== prev.imageUrl ||
                updatedMarker.thumbnail !== prev.thumbnail;

            if (hasChanged) {
                return { ...updatedMarker, isCluster: false, totalCount: prev.totalCount };
            }

            return prev;
        });
    }, [allMarkers, buildClusterGroups]);

    // Initialize MarkerClusterer
    useEffect(() => {
        if (!map || !hasAdvancedMarker) return;
        if (!clusterer.current) {
            const AdvancedMarkerCtor = (google as any)?.maps?.marker?.AdvancedMarkerElement;
            const renderer = {
                render: (cluster: Cluster, _stats: unknown, mapInstance: google.maps.Map) => {
                    const div = document.createElement('div');
                    div.className = 'cluster-icon';
                    div.textContent = cluster.count.toString();

                    if (!AdvancedMarkerCtor) {
                        // Fallback difensivo: marker classico se Advanced Marker non disponibile.
                        return new google.maps.Marker({
                            position: cluster.position,
                            label: `${cluster.count}`,
                            map: mapInstance,
                            zIndex: 1000 + cluster.count,
                        }) as unknown as Marker;
                    }

                    const icon = new AdvancedMarkerCtor({
                        position: cluster.position,
                        content: div,
                        map: mapInstance,
                        zIndex: 1000 + cluster.count,
                    }) as Marker;

                    icon.addListener('click', (e: google.maps.MapMouseEvent) => {
                        if (e.domEvent) {
                            e.domEvent.stopPropagation(); // Prevent map click
                        }

                        const itemsInCluster = cluster.markers
                            .map(getMarkerData)
                            .filter((item): item is MarkerData => Boolean(item));
                        const groups = buildClusterGroups(itemsInCluster);
                        setSelectedItem({
                            isCluster: true,
                            lat: cluster.position.lat(),
                            lng: cluster.position.lng(),
                            markerIds: itemsInCluster.map((item) => item.id),
                            totalCount: itemsInCluster.length,
                            groups,
                        });
                    });

                    return icon;
                }
            };
            clusterer.current = new MarkerClusterer({ map, renderer });
        }
    }, [map, buildClusterGroups, hasAdvancedMarker]);

    // Update markers in the clusterer
    useEffect(() => {
        if (!map || !clusterer.current || !hasAdvancedMarker) return;

        const AdvancedMarkerCtor = (google as any)?.maps?.marker?.AdvancedMarkerElement;
        if (!AdvancedMarkerCtor) return;

        try {
            const currentMarkerInstances = markersRef.current;
            const newMarkerDataMap = new Map(allMarkers.map(m => [m.id, m]));
            const markersToAdd: ClustererMarker[] = [];
            const markersToRemove: ClustererMarker[] = [];

            // Identify markers to remove
            for (const markerId in currentMarkerInstances) {
                if (!newMarkerDataMap.has(markerId)) {
                    markersToRemove.push(currentMarkerInstances[markerId]);
                    delete currentMarkerInstances[markerId];
                }
            }

            // Create a mutable copy to handle re-creation logic safely
            const markersToProcess = [...allMarkers];

            // First pass: Identify markers that have moved and mark them for re-creation
            for (const markerId in currentMarkerInstances) {
                const instance = currentMarkerInstances[markerId];
                const newData = newMarkerDataMap.get(markerId);
                if (instance && newData) {
                    const pos = instance.position;
                    let lat, lng;
                    if (pos && typeof (pos as google.maps.LatLng).lat === 'function') {
                        lat = (pos as google.maps.LatLng).lat();
                        lng = (pos as google.maps.LatLng).lng();
                    } else if (pos) {
                        lat = (pos as google.maps.LatLngLiteral).lat;
                        lng = (pos as google.maps.LatLngLiteral).lng;
                    }

                    if (lat !== newData.lat || lng !== newData.lng) {
                        markersToRemove.push(instance);
                        delete currentMarkerInstances[markerId];
                    }
                }
            }

            // Second pass: Process all markers for adding or updating content
            markersToProcess.forEach(markerData => {
                const existingMarker = currentMarkerInstances[markerData.id];

                if (!existingMarker) {
                    // Add new marker
                    const markersAtSamePosition = getMarkersAtSamePosition(markerData);
                    const options: google.maps.marker.AdvancedMarkerElementOptions = {
                        position: { lat: markerData.lat, lng: markerData.lng },
                        gmpDraggable: false,
                        content: createDefaultPin(
                            markerData.filename,
                            markerData.id === movingMarkerId,
                            markerData.id === sourceMarkerId
                        ),
                    };
                    if (markersAtSamePosition.length > 1) {
                        const div = document.createElement('div');
                        div.className = 'cluster-icon';
                        div.textContent = markersAtSamePosition.length.toString();
                        options.content = div;
                    }

                    const marker = new AdvancedMarkerCtor(options) as ClustererMarker;
                    marker.markerData = markerData;

                    marker.addListener('click', (e: google.maps.MapMouseEvent) => {
                        if (e.domEvent) {
                            e.domEvent.stopPropagation();
                        }
                        const currentData = marker.markerData ?? markerData;
                        handleMarkerSelect(currentData);
                    });

                    markersToAdd.push(marker);
                    currentMarkerInstances[markerData.id] = marker;
                } else {
                    // Marker exists, check if its position or content needs updating
                    const gmpMarker = existingMarker as google.maps.marker.AdvancedMarkerElement;
                    existingMarker.markerData = markerData;
                    const neighbors = getMarkersAtSamePosition(markerData);

                    if (neighbors.length > 1) {
                        const div = (gmpMarker.content as HTMLElement) || document.createElement('div');
                        div.className = 'cluster-icon';
                        div.textContent = neighbors.length.toString();
                        gmpMarker.content = div;
                    } else {
                        gmpMarker.content = createDefaultPin(
                            markerData.filename,
                            markerData.id === movingMarkerId,
                            markerData.id === sourceMarkerId
                        );
                    }
                }
            });

            if (markersToRemove.length > 0) {
                clusterer.current.removeMarkers(markersToRemove, true);
            }
            if (markersToAdd.length > 0) {
                clusterer.current.addMarkers(markersToAdd);
            }

            // Force a repaint of the clusterer to reflect content changes (e.g., single to multi-marker)
            clusterer.current.render();
        } catch (error) {
            console.error('[MAP] Marker initialization/update failed:', error);
        }

    }, [allMarkers, buildClusterGroups, handleMarkerSelect, movingMarkerId, map, sourceMarkerId, hasAdvancedMarker]);

    useEffect(() => {
        if (!selectedItem) {
            setActiveActionsForId(null);
            previewInteractionRef.current = false;
        }
    }, [selectedItem]);

    // Global 'click outside' listener removed to allow map interaction while popup is open.
    // The popup is now only closed via the close button or by selecting a different marker.
    /*
    useEffect(() => {
        const handleOutsideClick = (event: PointerEvent) => {
            if (previewPanelRef.current && !previewPanelRef.current.contains(event.target as Node)) {
                 // Logic removed to persist popup
            }
        };
        window.addEventListener('pointerdown', handleOutsideClick, true);
        return () => window.removeEventListener('pointerdown', handleOutsideClick, true);
    }, []);
    */

    const renderActions = (marker: MarkerData) => {
        const stopAll = (e: React.MouseEvent | React.PointerEvent | React.TouchEvent) => {
            haltMapEvent(e);
        };

        const handleView = (e: any) => {
            stopAll(e);
            console.log('[MAP][ACTION] View clicked', marker.id, marker.filename);
            onImageClick(marker);
        };

        const handleMove = async (e: any) => {
            stopAll(e);
            console.log('[MAP][ACTION] Move clicked', marker.id, marker.filename);
            // Inline the logic from handleMoveMarkerClick to ensure we await properly before closing
            // or at least trigger it.
            // Actually, handleMoveMarkerClick handled it well, but let's be explicit and remove internal close calls inside it if we can,
            // or just replicate the sequence: Action First, Then UI Update.

            // Logic form handleMoveMarkerClick:
            if (!marker || marker.draggable === false) return;
            try {
                const allowed = await onRequestMoveMarker(marker);
                if (!allowed) return;
            } catch { return; }

            setSelectedItem(null);
            setActiveActionsForId(null);
            setGhostMoveTarget(marker);
            setMovingMarkerId(marker.id);
            if (marker.lat && marker.lng && map) {
                map.panTo?.({ lat: marker.lat, lng: marker.lng });
            }
        };

        const handleDelete = (e: any) => {
            stopAll(e);
            console.log('[MAP][ACTION] Delete clicked', marker.id, marker.filename);
            // Don't close immediately to see if that helps propagation? 
            // Actually, we must let React event bubble to button handler fully.
            // But setSelectedItem triggers re-render.
            onRequestDeleteMarker(marker);
            // Delay closing slightly or rely on external close?
            // Let's close after action dispatch.
            setSelectedItem(null);
            setActiveActionsForId(null);
        };

        const handleMoreLikeThis = (e: any) => {
            stopAll(e);
            console.log('[MAP][ACTION] More like this clicked', marker.id, marker.filename);
            onRequestMoreLikeThis(marker);
            setSelectedItem(null);
            setActiveActionsForId(null);
        };

        const handleDeleteDuplicates = (e: any) => {
            stopAll(e);
            handleDeleteDuplicatesClick(marker);
        };

        const attachPressHandlers = (fn: (e: any) => void) => ({
            onMouseDown: fn,
            onPointerDown: fn,
            onTouchStart: fn,
            onClick: fn,
        });

        return (
            <div
                className="map-preview-actions"
                onClick={stopMapEvent}
                onPointerDown={haltMapEvent}
                onMouseDown={haltMapEvent}
                onTouchStart={haltMapEvent}
            >
                <div className="map-preview-meta">
                    <span className="map-preview-name">{marker.filename}</span>
                </div>
                <div className="map-preview-actions__buttons">
                    <button
                        type="button"
                        onClick={handleView}
                        className="map-preview-action"
                    >
                        View
                    </button>
                    <button
                        type="button"
                        onClick={handleMove}
                        className="map-preview-action"
                        disabled={marker.draggable === false}
                    >
                        Move
                    </button>
                    <button
                        type="button"
                        onClick={handleDelete}
                        className="map-preview-action map-preview-action--danger"
                    >
                        Delete
                    </button>
                    {showMoreLikeThis && (
                        <button
                            type="button"
                            onClick={handleMoreLikeThis}
                            className="map-preview-action"
                        >
                            More like this
                        </button>
                    )}
                    {isDuplicateMode && marker.contentHash && (
                        <button
                            type="button"
                            onClick={handleDeleteDuplicates}
                            className="map-preview-action"
                        >
                            Delete duplicates
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const buildSelectionList = (): MarkerData[] => {
        if (!selectedItem) return [];
        if (!selectedItem.isCluster) {
            return [selectedItem];
        }
        const markers = selectedItem.groups.flatMap((group) => group.markers);
        const unique = new Map<string, MarkerData>();
        markers.forEach((m) => {
            if (!unique.has(m.id)) {
                unique.set(m.id, m);
            }
        });
        return Array.from(unique.values());
    };

    const markersForPreview = buildSelectionList();

    return (
        <GoogleMap
            defaultCenter={center}
            defaultZoom={4}
            mapId={GOOGLE_MAPS_MAP_ID}
            mapTypeId="hybrid"
            style={{ width: '100%', height: '100%' }}
            gestureHandling={'greedy'}
            streetViewControl={false}
            fullscreenControl={false}
            clickableIcons={false}
        >
            {/* Markers are now managed by MarkerClusterer */}

            {/* Unified Preview Overlay - Renders for ALL users (Mouse & Touch) to keep popup centered and within bounds */}
            {selectedItem && (
                <div className="map-preview-overlay">
                    <div
                        className="map-preview-panel"
                        ref={previewPanelRef}
                        onClick={stopMapEvent}
                        onPointerDown={stopMapEvent}
                        onTouchStart={stopMapEvent}
                    >
                        <div className="map-preview-header">
                            <span className="map-preview-chip">
                                {selectedItem.isCluster ? `${selectedItem.totalCount} photos` : 'Photo'}
                            </span>
                            <button type="button" className="map-preview-close" onClick={closePreview}>
                                ×
                            </button>
                        </div>
                        <div className={`map-preview-grid ${markersForPreview.length > 1 ? 'map-preview-grid--multi' : ''}`}>
                            {markersForPreview.map((marker) => {
                                const thumbSrc = resolveMarkerThumbnail(marker);
                                const isActive = activeActionsForId === marker.id;
                                return (
                                    <div
                                        key={marker.id}
                                        className={`map-preview-thumb ${isActive ? 'is-active' : ''}`}
                                        onClick={(e) => {
                                            stopMapEvent(e);
                                            toggleActionsForMarker(marker);
                                        }}
                                    >
                                        {thumbSrc ? (
                                            <img src={thumbSrc} alt={marker.filename} />
                                        ) : (
                                            <div className="map-preview-thumb__placeholder" />
                                        )}
                                        <button
                                            type="button"
                                            className="map-preview-thumb__toggle"
                                            onClick={(e) => {
                                                haltMapEvent(e);
                                                toggleActionsForMarker(marker);
                                            }}
                                            aria-pressed={isActive}
                                            aria-label={isActive ? 'Hide actions' : 'Show actions'}
                                        >
                                            &#8942;
                                        </button>
                                        <div className="map-preview-thumb__label">{marker.filename}</div>
                                        {isActive && renderActions(marker)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {ghostMoveTarget && (
                <div className="map-move-overlay">
                    <div className="map-move-crosshair-wrapper">
                        <button
                            type="button"
                            className={`map-move-lock ${isCrosshairZoomLocked ? 'is-locked' : 'is-unlocked'}`}
                            onClick={() => setIsCrosshairZoomLocked((prev) => !prev)}
                            title={isCrosshairZoomLocked ? 'Zoom locked to crosshair' : 'Zoom follows pointer'}
                            aria-pressed={isCrosshairZoomLocked}
                        >
                            <span className="map-move-lock__icon" aria-hidden="true">
                                {isCrosshairZoomLocked ? '🔒' : '🔓'}
                            </span>
                            <span className="map-move-lock__label">
                                {isCrosshairZoomLocked ? 'Locked' : 'Unlocked'}
                            </span>
                        </button>
                        <div className="map-move-crosshair" />
                    </div>
                    <div className="map-move-controls">
                        <div className="map-move-text">
                            Move “{ghostMoveTarget.filename}”
                        </div>
                        <p className="map-move-hint">Pan/zoom the map to center the crosshair, then confirm.</p>
                        <div className="map-move-buttons">
                            <button type="button" className="map-move-button map-move-button--primary" onClick={handleGhostMoveConfirm}>
                                Move here
                            </button>
                            <button type="button" className="map-move-button" onClick={handleGhostMoveCancel}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </GoogleMap>
    );
};

export default React.memo(MapComponent);
